import React, { useEffect, useRef, useState } from 'react';
import { useCaptions } from '../context/CaptionContext';
import { useI18n } from '../../i18n';
import './LineSettingsPopup.css';

const LineSettingsPopup = ({ skeletonIndex, anchorRect, onClose }) => {
  const { t } = useI18n();
  const {
    captionData,
    updateSkeleton,
    updateSkeletonTiming,
    resetSkeleton,
    resetAllSkeletons
  } = useCaptions();

  const skeleton = captionData.skeleton[skeletonIndex];
  const popupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [overlapWarning, setOverlapWarning] = useState(null);
  const [hasUnsavedTimeChanges, setHasUnsavedTimeChanges] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);

  // Initialize timing values
  useEffect(() => {
    if (skeleton) {
      setStartTime(skeleton.start_time?.toFixed(2) || '0.00');
      setEndTime(skeleton.end_time?.toFixed(2) || '0.00');
    }
  }, [skeleton?.id]);

  // Position popup near the button
  useEffect(() => {
    if (anchorRect && popupRef.current) {
      const popup = popupRef.current;
      const popupRect = popup.getBoundingClientRect();

      const minTop = 8;
      const minLeft = 8;
      const margin = 8;

      // Calculate available space
      const spaceBelow = window.innerHeight - anchorRect.bottom - margin * 2;
      const spaceAbove = anchorRect.top - margin * 2;

      let top;
      let calculatedMaxHeight = null;

      // Try to position below first
      if (popupRect.height <= spaceBelow) {
        // Fits below
        top = anchorRect.bottom + margin;
        calculatedMaxHeight = spaceBelow;
      }
      // Try above
      else if (popupRect.height <= spaceAbove) {
        // Fits above
        top = anchorRect.top - popupRect.height - margin;
        calculatedMaxHeight = spaceAbove;
      }
      // Use the larger space with scrolling
      else {
        if (spaceBelow >= spaceAbove) {
          // More space below
          top = anchorRect.bottom + margin;
          calculatedMaxHeight = spaceBelow;
        } else {
          // More space above
          top = minTop;
          calculatedMaxHeight = spaceAbove;
        }
      }

      // Ensure top doesn't go negative
      if (top < minTop) {
        top = minTop;
      }

      // Handle horizontal positioning
      let left = anchorRect.left;

      // Keep within viewport horizontally
      const maxLeft = window.innerWidth - popupRect.width - margin;
      if (left < minLeft) {
        left = minLeft;
      } else if (left > maxLeft && maxLeft > minLeft) {
        left = maxLeft;
      }

      setPosition({ top, left });
      setMaxHeight(calculatedMaxHeight);
    }
  }, [anchorRect, overlapWarning]);

  if (!skeleton) return null;

  const currentSize = skeleton.size || 'medium';
  const currentPositionY = skeleton.positionY ?? 37;
  const isEmphasized = skeleton.emphasize || false;

  // Get adjacent skeletons for overlap detection
  const adjacentSkeletons = {
    prev: skeletonIndex > 0 ? captionData.skeleton[skeletonIndex - 1] : null,
    next: skeletonIndex < captionData.skeleton.length - 1 ? captionData.skeleton[skeletonIndex + 1] : null
  };

  const handleSizeChange = (size) => {
    updateSkeleton(skeletonIndex, { size });
  };

  const handlePositionYChange = (value) => {
    updateSkeleton(skeletonIndex, { positionY: parseInt(value, 10) });
  };

  const handleEmphasizeToggle = () => {
    updateSkeleton(skeletonIndex, { emphasize: !isEmphasized });
  };

  const handleTimeInputChange = (field, value) => {
    if (field === 'start_time') {
      setStartTime(value);
      const originalValue = skeleton.start_time?.toFixed(2) || '0.00';
      setHasUnsavedTimeChanges(value !== originalValue || endTime !== (skeleton.end_time?.toFixed(2) || '0.00'));
    } else {
      setEndTime(value);
      const originalValue = skeleton.end_time?.toFixed(2) || '0.00';
      setHasUnsavedTimeChanges(startTime !== (skeleton.start_time?.toFixed(2) || '0.00') || value !== originalValue);
    }
    setOverlapWarning(null);
  };

  const checkSkeletonOverlap = (newStartTime, newEndTime) => {
    const startNum = parseFloat(newStartTime);
    const endNum = parseFloat(newEndTime);

    if (isNaN(startNum) || isNaN(endNum) || startNum < 0 || endNum < 0) {
      return { type: 'invalid', message: t('Invalid time values') };
    }

    // No longer restricting end time to be after start time - users have full freedom

    return null;
  };

  const handleApplyTimeChanges = () => {
    const startNum = parseFloat(startTime);
    const endNum = parseFloat(endTime);

    const overlap = checkSkeletonOverlap(startTime, endTime);
    if (overlap) {
      setOverlapWarning(overlap);
      return;
    }

    // Apply timing change with word auto-adjustment
    if (updateSkeletonTiming) {
      updateSkeletonTiming(skeletonIndex, startNum, endNum);
      setHasUnsavedTimeChanges(false);
      setOverlapWarning(null);
    }
  };

  const handleAutoAdjust = () => {
    if (!overlapWarning) return;

    const startNum = parseFloat(startTime);
    const endNum = parseFloat(endTime);

    // Apply the timing change
    if (updateSkeletonTiming) {
      updateSkeletonTiming(skeletonIndex, startNum, endNum, {
        autoAdjustAdjacent: true,
        overlapInfo: overlapWarning
      });
      setHasUnsavedTimeChanges(false);
      setOverlapWarning(null);
    }
  };

  const handleCancelChange = () => {
    setStartTime(skeleton.start_time?.toFixed(2) || '0.00');
    setEndTime(skeleton.end_time?.toFixed(2) || '0.00');
    setOverlapWarning(null);
    setHasUnsavedTimeChanges(false);
  };

  const handleReset = () => {
    resetSkeleton(skeletonIndex);
    setStartTime(skeleton.start_time?.toFixed(2) || '0.00');
    setEndTime(skeleton.end_time?.toFixed(2) || '0.00');
    setHasUnsavedTimeChanges(false);
  };

  const handleResetAll = () => {
    resetAllSkeletons();
    onClose();
  };

  return (
    <div
      className="line-settings-popup"
      ref={popupRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 1000,
        maxHeight: maxHeight ? `${maxHeight}px` : 'calc(100vh - 40px)',
        overflowY: 'auto'
      }}
    >
      {/* Timing Controls */}
      <div className="settings-section timing-section">
        <span className="settings-label">{t('Chunk Timing')}</span>
        <div className="time-range-visual" dir="ltr" lang="en">
          <div className="time-block start-block">
            <div className="time-block-header">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              <span>{t('START')}</span>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={startTime}
              onChange={(e) => handleTimeInputChange('start_time', e.target.value)}
              className={`time-input ${overlapWarning?.type === 'overlap_prev' ? 'warning' : ''}`}
            />
            <span className="time-unit">{t('sec')}</span>
          </div>

          <div className="time-duration">
            <div className="duration-line"></div>
            <span className="duration-value">
              {((parseFloat(endTime) || 0) - (parseFloat(startTime) || 0)).toFixed(2)}s
            </span>
            <div className="duration-line"></div>
          </div>

          <div className="time-block end-block">
            <div className="time-block-header">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="6" y="4" width="12" height="16" rx="2" />
              </svg>
              <span>{t('END')}</span>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={endTime}
              onChange={(e) => handleTimeInputChange('end_time', e.target.value)}
              className={`time-input ${overlapWarning?.type === 'overlap_next' ? 'warning' : ''}`}
            />
            <span className="time-unit">{t('sec')}</span>
          </div>
        </div>

        {/* Apply Button */}
        {hasUnsavedTimeChanges && !overlapWarning && (
          <div className="time-apply-row">
            <button className="time-apply-btn" onClick={handleApplyTimeChanges}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {t('Apply Timing')}
            </button>
            <button className="time-reset-btn" onClick={handleCancelChange}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* Overlap Warning */}
        {overlapWarning && (
          <div className="overlap-warning">
            <div className="warning-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className="warning-content">
              <p className="warning-message">{overlapWarning.message}</p>
              <p className="warning-hint">
                {overlapWarning.type === 'overlap_prev'
                  ? t('Auto-adjust will shorten the previous chunk')
                  : overlapWarning.type === 'overlap_next'
                  ? t('Auto-adjust will delay the next chunk')
                  : ''}
              </p>
              {overlapWarning.type !== 'invalid' && (
                <div className="warning-actions">
                  <button className="warning-btn auto-adjust" onClick={handleAutoAdjust}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                    </svg>
                    {t('Auto-adjust')}
                  </button>
                  <button className="warning-btn cancel" onClick={handleCancelChange}>
                    {t('Cancel')}
                  </button>
                </div>
              )}
              {overlapWarning.type === 'invalid' && (
                <div className="warning-actions">
                  <button className="warning-btn cancel" onClick={handleCancelChange}>
                    {t('OK')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Size Selector */}
      <div className="settings-section">
        <span className="settings-label">{t('Size')}</span>
        <div className="size-selector">
          {['small', 'medium', 'large'].map((size) => (
            <button
              key={size}
              className={`size-btn ${currentSize === size ? 'selected' : ''}`}
              onClick={() => handleSizeChange(size)}
            >
              {t(size.charAt(0).toUpperCase() + size.slice(1))}
            </button>
          ))}
        </div>
      </div>

      {/* Position Y Slider */}
      <div className="settings-section">
        <span className="settings-label">{t('Position Y')}</span>
        <div className="position-slider-row">
          <input
            type="range"
            min="0"
            max="100"
            value={currentPositionY}
            onChange={(e) => handlePositionYChange(e.target.value)}
            className="position-slider"
          />
          <div className="position-value">
            <span>{currentPositionY}</span>
            <span className="percent-sign">%</span>
          </div>
        </div>
      </div>

      {/* Emphasize Toggle */}
      <div className="settings-section">
        <span className="settings-label">{t('Emphasize')}</span>
        <div className="toggle-row">
          <span className="toggle-text">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7V4h16v3" />
              <path d="M9 20h6" />
              <path d="M12 4v16" />
            </svg>
            {t('Emphasize text')}
          </span>
          <button 
            className={`toggle-switch ${isEmphasized ? 'active' : ''}`}
            onClick={handleEmphasizeToggle}
          >
            <span className="toggle-handle" />
          </button>
        </div>
      </div>

      {/* Reset Actions */}
      <div className="settings-actions">
        <button className="reset-btn" onClick={handleReset}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          {t('Reset')}
        </button>
        <button className="reset-btn reset-all" onClick={handleResetAll}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 11-9-9c2.52 0 4.85 1.03 6.5 2.74L21 3" />
            <path d="M21 3v5h-5" />
          </svg>
          {t('Reset All Lines')}
        </button>
      </div>

    </div>
  );
};

export default LineSettingsPopup;





